import { useGetUserGroupsQuery } from 'store/services/flow/userGroups';
import { useState, useMemo, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';

import type { ModalsType } from 'types';
import { Tabs } from 'components/mui/common/Tabs';

import { ContactsTable } from 'components/user/Contacts/ContactsTable';
import { ActionModal } from 'components/mui/common/ActionModal';

interface Props extends ModalsType {
  title: string;
  onSubmit: (selectedUsers: string[]) => void;
  roleIds: string[];
  selectedUserIds: string[];
}

export const UsersFilterModal = memo(({ open, title, onClose, onSubmit, roleIds, selectedUserIds }: Props) => {
  const { t } = useTranslation();
  const [selectedUsers, setSelectedUsers] = useState<string[]>(selectedUserIds);
  const [selectedTab, setSelectedTab] = useState<string>(t('user.contacts.groups.all'));
  const { data: { userGroups = [] } = {} } = useGetUserGroupsQuery();

  const groups = useMemo(() => [{ id: '', name: t('user.contacts.groups.all'), isAuthor: false }, ...userGroups], [t, userGroups]);

  const handleSelect = useCallback((users: string[]) => {
    setSelectedUsers(users);
  }, []);

  const tabs = useMemo(() => {
    return groups.map(({ name, id }) => ({
      id: name,
      label: name,
      path: '',
      content: (
        <ContactsTable
          onSelectUser={handleSelect}
          hideCheckbox={false}
          defaultSelectedRows={selectedUserIds}
          isMultiSelect={true}
          groupId={id}
          roleIds={roleIds}
          fetchUsersMethod="simple"
        />
      ),
    }));
  }, [groups, handleSelect, selectedUserIds, roleIds]);

  return (
    <ActionModal
      open={open}
      title={title}
      onClose={onClose}
      onCancel={onClose}
      onSubmit={() => onSubmit(selectedUsers)}
      maxWidth="lg"
      submitText={t('common.select')}
      sx={{
        // To ensure it is above the Filter drawer
        zIndex: 12001,
      }}>
      <Tabs tabs={tabs} selectedTab={selectedTab} onTabClick={(tab) => setSelectedTab(tab as string)} />
    </ActionModal>
  );
});
